/* ===========================================================================
   ROCKSOLAR product finder — the catalogue adapter
   ---------------------------------------------------------------------------
   assets/rs-finder-catalog.js

   Reads the JSON that snippets/finder-data.liquid rendered from live products
   and turns it into the four tables the engine expects, then leaves them on
   window.RS_TABLES for assets/rs-finder-engine.js, which loads next.

   That split is the whole point. The engine never names a product and never
   reads a metafield; it computes what the customer needs as numbers and then
   scans these tables. Add a product in Shopify with its metafields filled and
   it competes on the next page load. Nothing here changes, in either
   direction.

   The four tables:
     CATALOG     solar systems        <- data.systems
     PUMPCURVES  well and transfer    <- data.pumps
     POOL        swimming pool        <- data.pool
     RACKING     mounting             <- data.racking
   =========================================================================== */
(function () {
  "use strict";

  var node = document.getElementById("finder-data");
  if (!node) { return; }

  var data;
  try {
    data = JSON.parse(node.textContent);
  } catch (e) {
    var host = document.getElementById("selBody");
    if (host) {
      host.innerHTML = '<p class="sel-help">The finder could not read the product ' +
        'catalogue just now. <a href="/collections/all">Browse the range</a> or ' +
        'call us and we will size it with you.</p>';
    }
    if (window.console) { console.error("[finder] catalogue did not parse:", e); }
    return;
  }

  /* ---------------------------------------------------------------------
     Money. The storefront serves each market in its own currency, so the
     figures in the JSON are already converted — a Canadian visitor gets
     CAD. The symbol must follow, or the page prints US dollars over
     Canadian numbers.
     --------------------------------------------------------------------- */
  var CURRENCY = data.currency || "USD";
  var fmt;
  try {
    fmt = new Intl.NumberFormat(undefined, { style: "currency", currency: CURRENCY });
  } catch (e2) { fmt = null; }
  function money(n) {
    if (n == null || isNaN(n)) { return "—"; }
    if (fmt) { return fmt.format(n); }
    return "$" + Number(n).toLocaleString("en-US",
      { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  /* =====================================================================
     SOLAR SYSTEMS
     ===================================================================== */

  /* The documents a system can offer. The manual and datasheet are files the
     store publishes per product, so they are read from it. The wiring diagram
     and the commissioning record are drawn on demand from the same data this
     adapter is reading, so any system with an inverter rating and an array
     has them — no file to upload, nothing to go stale. The page that draws
     them can be moved in the theme editor; the finder section passes it in. */
  var DOCS_PAGE = (window.RS_LINKS && window.RS_LINKS.documents) || "/pages/system-documents";
  function systemDocs(s) {
    var d = { manual: s.manualUrl, datasheet: s.datasheetUrl };
    if (s.kw != null && s.pvKw != null && s.handle) {
      var base = DOCS_PAGE + "?p=" + encodeURIComponent(s.handle) + "&doc=";
      d.wiring = base + "wiring";
      d.commissioning = base + "commissioning";
    }
    return d;
  }

  /* Nameplate wording, derived from what the product publishes rather than
     stored as a string, so a new system gets a sensible label with no edit. */
  function voltLabel(s) {
    if (s.family === "gridtie") { return "Grid-tie"; }
    if (s.family === "hybrid")  { return "Hybrid with storage"; }
    return s.split ? "120/240 Vac" : "48 V";
  }

  /* Several systems sell the same inverter and array against two different
     battery packs, one variant each. The variant title is where that capacity
     is written today ("2. 8 x Growatt 51.2V ALP 5.0 5Kwh Total 40.00Kwh"), so
     it is read from there, taking the LAST figure because that is the total
     rather than the per-module capacity.

     This is the one place the finder reads a human-typed string. Give the
     variants their own capacity metafield and this falls away. Until then a
     title that states no capacity simply inherits the product's figure, which
     is the safe direction to fail. */
  function variantKwh(title, fallback) {
    var all = String(title || "").match(/([\d]+(?:\.[\d]+)?)\s*k\s*wh/gi);
    if (!all || !all.length) { return fallback; }
    var last = all[all.length - 1].match(/([\d]+(?:\.[\d]+)?)/);
    return last ? parseFloat(last[1]) : fallback;
  }

  var CATALOG = (data.systems || []).map(function (s) {
    var buyable = (s.variants || []).filter(function (v) { return v.available; });
    var pool = buyable.length ? buyable : (s.variants || []);
    pool = pool.slice().sort(function (a, b) { return a.price - b.price; });

    var first = pool[0] || {};
    var row = {
      id:        s.handle,
      family:    s.family,
      name:      s.title,
      url:       s.url,
      image:     s.image,
      price:     first.price != null ? first.price : s.price,
      stock:     first.qty != null ? first.qty : s.stock,
      variantId: first.id,
      kw:        s.kw,
      pvKw:      s.pvKw,
      kwh:       variantKwh(first.title, s.kwh),
      split:     s.split,
      busV:      s.busV,
      battBrand: s.battBrand,
      noDocs:    !s.hasManual,
      docs:      systemDocs(s)
    };
    row.volt = voltLabel(s);

    /* The second battery option, when there is one and it is genuinely a
       different capacity. On the 30 kW it is both smaller and dearer than the
       first; the engine says so rather than dressing it as a trade-off. */
    for (var i = 1; i < pool.length; i++) {
      var k = variantKwh(pool[i].title, null);
      if (k != null && k !== row.kwh) {
        row.alt2 = {
          label: kwhLabel(k) + " battery instead",
          price: pool[i].price,
          kwh:   k,
          variantId: pool[i].id
        };
        break;
      }
    }
    return row;
  });

  /* =====================================================================
     WELL AND TRANSFER PUMPS
     Row shape the engine reads:
       [name, kind, drive, hp, isKit, isPackage, [[label, gpm, head, price, stock], ...]]
     A pump with no published duty contributes an empty model list, so it can
     never be recommended. That is deliberate: a silent wrong recommendation
     is worse than a missing one.
     ===================================================================== */
  var PUMPCURVES = (data.pumps || []).map(function (p) {
    return [
      p.title, p.kind, p.drive, p.hp, !!p.isKit, !!p.isPackage,
      (p.models || []).map(function (m) {
        return [m.label, m.gpm, m.head, m.price, m.qty, m.variantId];
      }),
      /* everything that is about the listing rather than the duty */
      { url: p.url, image: p.image, docs: { manual: p.manualUrl, datasheet: p.datasheetUrl } }
    ];
  });

  /* Annual consumption of the conventional AC pump this one replaces, used
     only by the savings estimate. It is an engineering assumption, not a
     product figure, so it is keyed on horsepower and interpolated for sizes
     not in the table rather than being written against a product name. */
  var HP_KWH = [[0.5, 2000], [1.07, 2500], [1.34, 3000], [1.61, 3300], [2.14, 4000], [3.0, 4500]];
  function poolKwh(hp) {
    if (hp == null) { return 2500; }
    if (hp <= HP_KWH[0][0]) { return HP_KWH[0][1]; }
    for (var i = 1; i < HP_KWH.length; i++) {
      if (hp <= HP_KWH[i][0]) {
        var a = HP_KWH[i - 1], b = HP_KWH[i];
        return Math.round(a[1] + (b[1] - a[1]) * (hp - a[0]) / (b[0] - a[0]));
      }
    }
    var last = HP_KWH[HP_KWH.length - 1];
    return Math.round(last[1] * hp / last[0]);
  }

  /* =====================================================================
     SWIMMING POOL PUMPS

     Twelve listings, six sizes. Each size sells as a pump or as a kit with
     panels, and each of those sells solar-only or hybrid AC/DC as a variant.
     The engine wants one row per size with all four prices on it, so the
     twelve are folded back into six here.

     Which listing is the kit comes from custom.pump_is_kit; which variant is
     the hybrid comes from the data layer. Neither is guessed from the title.
     ===================================================================== */
  function poolPrice(product, wantHybrid) {
    if (!product) { return null; }
    var vs = product.variants || [];
    for (var i = 0; i < vs.length; i++) {
      if (!!vs[i].hybrid === wantHybrid) {
        return { p: vs[i].price, s: vs[i].qty, variantId: vs[i].id };
      }
    }
    return null;
  }

  var POOL = (function () {
    var bySize = {};
    (data.pool || []).forEach(function (p) {
      var key = String(p.hp);
      if (!bySize[key]) { bySize[key] = { hp: p.hp, pump: null, kit: null, spec: p }; }
      bySize[key][p.isKit ? "kit" : "pump"] = p;
    });

    return Object.keys(bySize).map(function (key) {
      var g = bySize[key];
      var spec = g.pump || g.kit;
      var dcPump = poolPrice(g.pump, false), dcKit = poolPrice(g.kit, false);
      var hyPump = poolPrice(g.pump, true),  hyKit = poolPrice(g.kit, true);

      return {
        hp:    String(g.hp),
        gpm:   spec.gpm,
        head:  spec.head,
        port:  spec.port,
        kwh:   poolKwh(g.hp),
        /* the listing title without its "for 20000 Gallon Pool" tail, because
           the finder states the pool size itself and would otherwise say it twice */
        base:  String(spec.title).replace(/\s+for\s+[\d,]+\s*gallon\s+pool\s*$/i, ""),
        url:   spec.url,
        image: spec.image,
        docs:  { manual: spec.manualUrl, datasheet: spec.datasheetUrl },
        dc:    (dcPump || dcKit) ? { pump: dcPump, kit: dcKit } : null,
        hy:    (hyPump || hyKit) ? { pump: hyPump, kit: hyKit } : null
      };
    })
    /* ascending, because the engine walks the list and takes the first size
       whose curve delivers the required flow at the customer's system head */
    .sort(function (a, b) { return parseFloat(a.hp) - parseFloat(b.hp); })
    .filter(function (r) { return r.dc || r.hy; });
  }());

  /* =====================================================================
     RACKING — the one thing the finder offers as an addition, because it is
     the one part no kit includes and no install can do without.
     ===================================================================== */
  var SURFACE_LABEL = {
    pitched:    "Pitched / shingle roof",
    metal:      "Metal roof",
    corrugated: "Corrugated metal roof",
    ground:     "Ground or wall",
    flat:       "Flat roof, RV or trailer"
  };

  var RACKING = (data.racking || []).map(function (r) {
    var packs = (r.packs || []).filter(function (x) { return x.available; });
    var base = {
      name:    r.title,
      surface: SURFACE_LABEL[r.surface] || r.surface,
      note:    r.note || "",
      url:     r.url,
      image:   r.image
    };
    if (r.mode === "piece" && packs.length) {
      /* The data layer publishes a per-panel price and a panel-count stock.
         The engine counts brackets, so convert back to the unit the customer
         actually adds to the cart. */
      var u = packs[0].unitsPerPanelSet || 1;
      base.kind      = "piece";
      base.perPanel  = u;
      base.price     = Math.round(packs[0].price / u * 100) / 100;
      base.stock     = packs[0].qty * u;
      base.variantId = packs[0].variantId;
    } else {
      base.kind  = "package";
      base.packs = packs.map(function (x) { return [x.panels, x.price, x.qty, x.variantId]; });
    }
    return base;
  }).filter(function (r) {
    return r.kind === "piece" ? r.stock > 0 : r.packs.length > 0;
  });

  /* Racking is priced against a panel count, so the only rule left is
     "which pack covers this many panels". */
  function rackFor(r, n) {
    if (r.kind === "piece") {
      var pieces = n * r.perPanel;
      return { panels: n, price: Math.round(pieces * r.price * 100) / 100, stock: r.stock,
               label: pieces + " × " + (r.perPanel === 1 ? "set" : "bracket"),
               exact: true, short: r.stock < pieces, variantId: r.variantId, qty: pieces };
    }
    for (var i = 0; i < r.packs.length; i++) {
      if (r.packs[i][0] >= n) {
        return { panels: r.packs[i][0], price: r.packs[i][1], stock: r.packs[i][2],
                 label: r.packs[i][0] + "-panel pack", exact: r.packs[i][0] === n,
                 short: r.packs[i][2] <= 0, variantId: r.packs[i][3], qty: 1 };
      }
    }
    return null;
  }

  /* Inverter ratings are nominal, so a requirement is allowed to exceed the
     nameplate by this much before the next size up is forced. */
  var KW_TOL = 1.05;

  /* The engine reads the catalog through these two functions only. */
  function catalogFamily(family) {
    return CATALOG.filter(function (s) { return s.family === family && s.stock > 0; });
  }
  /* Smallest system in a family that meets every stated requirement.
     Requirements are only tested where the catalog publishes the figure — a
     product with no kWh cannot be sized on storage, so it is excluded from any
     search that asks for storage rather than silently passing it. */
  function chooseSystem(family, need) {
    var pool = catalogFamily(family).filter(function (s) {
      /* A solar system has an array. The two Growatt "backup power systems"
         are an inverter, a battery and a transfer switch with no panels at
         all — real products, but not an answer to "what solar system should I
         buy". They are excluded by their own data rather than by name, so if
         panels are ever added to those kits they qualify again automatically. */
      if (s.pvKw == null) { return false; }
      if (s.kw == null || s.kw * KW_TOL < need.kw) { return false; }
      if (need.kwh) { if (s.kwh == null || s.kwh < need.kwh) { return false; } }
      if (need.split && !s.split) { return false; }
      return true;
    });
    pool.sort(function (a, b) { return a.kw - b.kw || a.price - b.price; });
    if (pool.length) { return { sys: pool[0], short: false }; }
    /* Nothing in the range reaches. Return the largest we do ship and say so —
       never silently hand back something too small. */
    var all = catalogFamily(family).filter(function (s) {
      return s.kw != null && (!need.kwh || s.kwh != null);
    });
    all.sort(function (a, b) { return b.kw - a.kw || b.price - a.price; });
    return all.length ? { sys: all[0], short: true } : { sys: null, short: true };
  }

  function kwLabel(v)  { return v == null ? "—" : (Math.round(v * 100) / 100) + " kW"; }
  function kwhLabel(v) { return v == null ? "—" : (Math.round(v * 100) / 100) + " kWh"; }

  /* Handed to the engine, and exposed for the test harness. */
  var TABLES = {
    CATALOG: CATALOG, PUMPCURVES: PUMPCURVES, POOL: POOL, RACKING: RACKING,
    rackFor: rackFor, catalogFamily: catalogFamily, chooseSystem: chooseSystem,
    kwLabel: kwLabel, kwhLabel: kwhLabel, money: money, currency: CURRENCY,
    KW_TOL: KW_TOL,
    /* Where the section wants the finder's "talk to us" and "see the range"
       links to go. Set from the section settings; the engine has defaults. */
    links: window.RS_LINKS || {}
  };
  /* The engine file loads after this one and starts itself from here. */
  window.RS_TABLES = TABLES;
}());
